import React, { useRef, useEffect, useState } from 'react';
import { Message, MessageAction } from '../types';
import { Sparkles, Mic, Hourglass, Gamepad2, ArrowUp, ArrowRight, Loader2 } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useKai } from '../contexts/KaiPresenceContext';
import { SimpleMarkdown } from './SimpleMarkdown';
import { KaiLogo } from './KaiLogo';
import { InsightCard } from './InsightCard';

type ChatMode = 'standard' | 'time_capsule' | 'origin_story';

interface Props {
    messages: Message[];
    onSendMessage: (text: string) => void;
    isLoading: boolean;
    onStartVoice: () => void;
    chatMode: ChatMode;
    onModeChange: (mode: ChatMode) => void;
    onAction?: (action: MessageAction, messageId: string) => Promise<void> | void;
}

// --- Internal Component: Creative Thinking Animations ---
const ThinkingIndicator: React.FC<{ mode: ChatMode; color: string }> = ({ mode, color }) => {
    const getDotColor = () => {
        switch (color) {
            case 'rose': return 'bg-rose-400';
            case 'emerald': return 'bg-emerald-400';
            case 'amber': return 'bg-amber-400';
            default: return 'bg-indigo-400';
        }
    };
    const dotColor = getDotColor();

    if (mode === 'origin_story') {
        return (
            <div className="flex items-center gap-3 px-2 py-1 min-h-[24px]">
                <div className="flex gap-1.5">
                    <div className="w-2 h-2 bg-amber-500 animate-bounce rounded-none" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-amber-500 animate-bounce rounded-none" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-amber-500 animate-bounce rounded-none" style={{ animationDelay: '300ms' }}></div>
                </div>
                <span className="text-[10px] font-mono font-bold text-amber-600 dark:text-amber-400 tracking-widest animate-pulse">LOADING</span>
            </div>
        );
    }

    if (mode === 'time_capsule') {
        return (
            <div className="flex items-center gap-3 px-2 py-1 min-h-[24px]">
                <div className="flex items-end gap-0.5 h-4">
                    <div className="w-1 bg-violet-400 rounded-sm animate-pulse" style={{ height: '60%', animationDuration: '0.8s' }}></div>
                    <div className="w-1 bg-violet-500 rounded-sm animate-pulse" style={{ height: '100%', animationDuration: '1.2s' }}></div>
                    <div className="w-1 bg-violet-400 rounded-sm animate-pulse" style={{ height: '40%', animationDuration: '1.0s' }}></div>
                    <div className="w-1 bg-violet-500 rounded-sm animate-pulse" style={{ height: '80%', animationDuration: '0.6s' }}></div>
                </div>
                <span className="text-[10px] font-mono text-violet-500 dark:text-violet-300 tracking-widest animate-pulse">TRANSMITTING...</span>
            </div>
        );
    }

    return (
        <div className="flex items-center justify-center px-1 py-1 min-h-[24px]">
            <div className="flex gap-1.5">
                <div className={`w-1.5 h-1.5 rounded-full animate-bounce ${dotColor}`} style={{ animationDelay: '0ms' }}></div>
                <div className={`w-1.5 h-1.5 rounded-full animate-bounce ${dotColor}`} style={{ animationDelay: '150ms' }}></div>
                <div className={`w-1.5 h-1.5 rounded-full animate-bounce ${dotColor}`} style={{ animationDelay: '300ms' }}></div>
            </div>
        </div>
    );
};

// --- Internal Component: Prism Card (Replaces Nebula Shard) ---
const PrismCard: React.FC<{
    title: string;
    subtext: string;
    prompt: string;
    color: 'amber' | 'blue' | 'violet' | 'emerald';
    onClick: () => void;
    index: number;
    themeMode: 'light' | 'dark';
    isShattering: boolean;
}> = ({ title, subtext, prompt, color, onClick, index, themeMode, isShattering }) => {
    
    // Dynamic Styles based on color
    const styles = {
        amber: { border: 'group-hover:border-amber-500/50', glow: 'bg-amber-500', text: 'group-hover:text-amber-500', decoration: 'bg-amber-500' },
        blue: { border: 'group-hover:border-blue-500/50', glow: 'bg-blue-500', text: 'group-hover:text-blue-500', decoration: 'bg-blue-500' },
        violet: { border: 'group-hover:border-violet-500/50', glow: 'bg-violet-500', text: 'group-hover:text-violet-500', decoration: 'bg-violet-500' },
        emerald: { border: 'group-hover:border-emerald-500/50', glow: 'bg-emerald-500', text: 'group-hover:text-emerald-500', decoration: 'bg-emerald-500' }
    };
    
    const activeStyle = styles[color];

    return (
        <button
            onClick={onClick}
            disabled={isShattering}
            className={`
                group relative flex flex-col justify-between p-4 md:p-8 rounded-[24px] md:rounded-[32px] text-left transition-all duration-500 ease-out 
                w-full md:h-[220px] h-full min-h-[140px]
                border backdrop-blur-2xl overflow-hidden
                ${themeMode === 'dark' 
                    ? 'bg-slate-900/80 border-slate-800' 
                    : 'bg-slate-50 border-slate-200'}
                ${activeStyle.border}
                ${isShattering ? 'scale-90 opacity-0 filter blur-lg pointer-events-none' : 'hover:shadow-2xl hover:-translate-y-2'}
            `}
            style={{ 
                // Staggered animation entrance
                animation: `fade-in-up 0.6s ease-out forwards ${index * 100}ms`,
                opacity: 0, // Start invisible for animation (CSS animation handles fadeIn)
                // Desktop Stagger Effect (Evens pushed down) - Only apply on large screens
                transform: !isShattering && typeof window !== 'undefined' && window.innerWidth >= 768 && index % 2 !== 0 ? 'translateY(24px)' : undefined
            }}
        >
            {/* The Light Source (Glow Orb) */}
            <div className={`absolute -top-20 -right-20 w-40 h-40 rounded-full blur-[80px] opacity-0 group-hover:opacity-40 transition-opacity duration-700 ${activeStyle.glow}`}></div>
            
            {/* Header */}
            <div className="relative z-10">
                <span className={`text-[10px] md:text-[9px] font-mono font-bold tracking-widest opacity-40 uppercase mb-1 md:mb-3 block ${themeMode === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>
                    {subtext}
                </span>
                <h3 className={`text-lg md:text-4xl font-serif font-medium tracking-tight transition-colors duration-300 leading-none ${themeMode === 'dark' ? 'text-slate-100' : 'text-slate-900'} ${activeStyle.text}`}>
                    {title}
                </h3>
            </div>

            {/* Prompt Preview */}
            <div className={`relative z-10 mt-3 md:mt-auto pt-3 md:pt-6 border-t border-dashed transition-colors duration-500 ${themeMode === 'dark' ? 'border-slate-700 group-hover:border-slate-600' : 'border-slate-200 group-hover:border-slate-300'}`}>
                <p className={`text-sm md:text-sm font-medium leading-relaxed opacity-80 line-clamp-2 ${themeMode === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>
                    "{prompt}"
                </p>
            </div>

            {/* Kinetic Arrow (Bottom Right) */}
            <div className={`absolute bottom-4 right-4 md:bottom-6 md:right-6 opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-500 ${activeStyle.text}`}>
                <ArrowRight size={20} className="md:w-6 md:h-6" />
            </div>
            
            {/* Deco Bar */}
            <div className={`absolute left-0 bottom-6 md:bottom-8 h-6 md:h-8 w-1 rounded-r-full transition-all duration-500 opacity-0 group-hover:opacity-100 ${activeStyle.decoration}`}></div>
        </button>
    );
};

export const ChatInterface: React.FC<Props> = ({ messages, onSendMessage, isLoading, onStartVoice, chatMode, onModeChange, onAction }) => {
    const [inputText, setInputText] = useState('');
    const [generatingId, setGeneratingId] = useState<string | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const { theme, getKaiTheme } = useTheme();
    const { setMood, triggerReaction, moveTo, state: kaiState } = useKai();
    const [isShattering, setIsShattering] = useState<number | null>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isLoading]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (inputText.trim() && !isLoading) {
            onSendMessage(inputText);
            setInputText('');
            setMood('thinking');
            triggerReaction('nod'); // Give visual feedback of understanding
        }
    };

    const handleStarterClick = async (text: string, index: number) => {
        setIsShattering(index);
        // Cinematic Shatter Delay
        await new Promise(r => setTimeout(r, 400));
        onSendMessage(text);
        setMood('thinking');
        triggerReaction('nod');
        // Reset isn't strictly necessary as component often re-renders with new messages, 
        // but good for safety if we clear chat.
        setTimeout(() => setIsShattering(null), 1000);
    };

    const handleInputFocus = () => {
        setMood('listening');
        moveTo('chat-input-area', { duration: 0, size: 60 });
    };

    const handleInputBlur = () => {
        setMood('idle');
        moveTo(null);
    };

    // Helper to get dynamic style classes based on Kai's active color
    const kaiTheme = getKaiTheme(kaiState.color);

    // Mapped Dynamic Styles using standardized Palette
    const dynamic = {
        prismBorder: `border-l-current text-${kaiTheme.name}-400 shadow-[inset_10px_0_20px_-10px_rgba(0,0,0,0.1)]`, 
        headerBorder: kaiTheme.border,
        headerBg: kaiTheme.glass,
        sendBtn: `${kaiTheme.primary} ${kaiTheme.shadow}`,
        textAccent: kaiTheme.text,
        iconBg: `${kaiTheme.bgLight} ${kaiTheme.text}`
    };

    const prismBorderColor = `border-l-${kaiTheme.name}-400`;

    // Get configuration based on centralized theme definitions
    const getConfig = () => {
        switch (chatMode) {
            case 'origin_story':
                return {
                    style: theme.colors.chat.originStory,
                    icon: <Gamepad2 size={20} />,
                    title: 'Past You (Age 8)',
                    subtitle: 'The Origin Story'
                };
            case 'time_capsule':
                return {
                    style: theme.colors.chat.timeCapsule,
                    icon: <Hourglass size={20} />,
                    title: 'Future You (2035)',
                    subtitle: 'Time Capsule Connection'
                };
            default:
                return {
                    style: theme.colors.chat.standard,
                    icon: <KaiLogo size={28} variant="face" animated={true} color={kaiState.color} />,
                    title: 'Kai',
                    subtitle: 'Ikigai Life Coach'
                };
        }
    };

    const { style, icon, title, subtitle } = getConfig();

    // --- THE PRISM PROTOCOL CONFIGURATION (V4.6: Direct Action) ---
    // UPDATED STRATEGY: Assertive statements ("I love...") that trigger instant data extraction.
    const prisms = [
        {
            title: "The Creator",
            subtext: "INIT_PASSION_CORE",
            prompt: "I feel most alive when I am creating something new from scratch. I love the process of building.",
            color: 'amber' as const
        },
        {
            title: "The Analyst",
            subtext: "SKILL_MATRIX_LOAD",
            prompt: "I am naturally good at solving complex problems and analyzing systems. It comes easily to me.",
            color: 'blue' as const
        },
        {
            title: "The Guide",
            subtext: "MISSION_VECTOR_SET",
            prompt: "I feel a deep drive to help others grow and reach their potential. That is my mission.",
            color: 'violet' as const
        },
        {
            title: "The Optimizer",
            subtext: "VIABILITY_CHECK",
            prompt: "I am obsessed with efficiency and making things work better. I want to be paid for optimization.",
            color: 'emerald' as const
        }
    ];

    return (
        // MOBILE FIX: Use rounded-none on mobile to fill screen perfectly without gaps
        <div className={`relative flex flex-col h-full md:rounded-[40px] overflow-hidden transition-all duration-700 ease-in-out ${style.container} shadow-2xl`}>

            {/* 1. Atmospheric Header - Fixed at Top */}
            <div className={`shrink-0 z-20 px-4 py-3 md:px-6 md:py-4 flex flex-row items-center justify-between gap-2 md:gap-4 transition-colors duration-500 backdrop-blur-md border-b ${chatMode === 'standard' ? `${dynamic.headerBg} ${dynamic.headerBorder}` : style.header}`}>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center text-white shadow-lg transition-all duration-500 overflow-hidden ring-4 ring-white/10 shrink-0 ${chatMode === 'standard' ? 'bg-transparent' : style.iconBg}`}>
                        {icon}
                    </div>
                    <div className="min-w-0">
                        <h2 className={`font-bold text-base md:text-lg transition-colors truncate ${style.text}`}>
                            {title}
                        </h2>
                        <div className={`text-[10px] md:text-xs font-bold uppercase tracking-wider flex items-center gap-1 truncate ${chatMode === 'standard' ? dynamic.textAccent : style.accent}`}>
                            <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse shrink-0"></span>
                            <span className="truncate">{subtitle}</span>
                        </div>
                    </div>
                </div>

                {/* Mode Toggles (Pill) */}
                <div className={`flex shrink-0 p-1 rounded-full border border-white/10 shadow-lg backdrop-blur-md ${theme.mode === 'dark' ? 'bg-black/40' : 'bg-white/60'}`}>
                    <button onClick={() => onModeChange('origin_story')} className={`w-8 h-8 md:w-9 md:h-9 rounded-full flex items-center justify-center transition-all ${chatMode === 'origin_story' ? 'bg-amber-500 text-white shadow-md scale-105' : 'text-slate-400 hover:text-amber-500'}`} title="Past">
                        <Gamepad2 size={16} />
                    </button>
                    <button onClick={() => onModeChange('standard')} className={`w-8 h-8 md:w-9 md:h-9 rounded-full flex items-center justify-center transition-all ${chatMode === 'standard' ? dynamic.sendBtn + ' scale-105 text-white' : 'text-slate-400 hover:text-indigo-600'}`} title="Present">
                        <Sparkles size={16} />
                    </button>
                    <button onClick={() => onModeChange('time_capsule')} className={`w-8 h-8 md:w-9 md:h-9 rounded-full flex items-center justify-center transition-all ${chatMode === 'time_capsule' ? 'bg-violet-600 text-white shadow-md scale-105' : 'text-slate-400 hover:text-violet-600'}`} title="Future">
                        <Hourglass size={16} />
                    </button>
                </div>
            </div>

            {/* 2. Messages Area (Stream) - Flex Grow */}
            <div className={`absolute inset-0 z-0 pointer-events-none transition-all duration-1000 ${style.bgEffect}`}></div>

            <div className="relative z-10 flex-1 overflow-y-auto custom-scrollbar px-4 md:px-10 py-6 space-y-8 scroll-smooth">
                {messages.length === 0 && (
                    <div className="flex flex-col items-center justify-center h-full pb-4 md:pb-20">
                        {chatMode === 'time_capsule' ? (
                            <div className="text-center animate-fade-in">
                                <Hourglass size={64} className={`mb-6 mx-auto opacity-80 ${theme.mode === 'dark' ? 'text-violet-500' : 'text-violet-400'}`} />
                                <h3 className={`text-3xl font-light mb-2 ${theme.mode === 'dark' ? 'text-violet-100' : 'text-violet-900'}`}>Link Established: 2035</h3>
                                <p className={`max-w-xs mx-auto text-sm ${theme.mode === 'dark' ? 'text-violet-300/60' : 'text-violet-700/60'}`}>
                                    You are speaking to your future self. Ask them how they did it.
                                </p>
                            </div>
                        ) : chatMode === 'origin_story' ? (
                            <div className="text-center animate-fade-in">
                                <Gamepad2 size={64} className={`mb-6 mx-auto ${theme.mode === 'dark' ? 'text-amber-500' : 'text-amber-400'}`} />
                                <h3 className={`text-3xl font-light mb-2 ${theme.mode === 'dark' ? 'text-amber-100' : 'text-amber-900'}`}>Memory Core Access</h3>
                                <p className={`max-w-xs mx-auto text-sm ${theme.mode === 'dark' ? 'text-amber-400/60' : 'text-amber-700/60'}`}>
                                    Reconnect with your 8-year-old self. Find the passions you left behind.
                                </p>
                            </div>
                        ) : (
                            // --- STANDARD MODE: THE PRISM PROTOCOL ---
                            <div className="w-full flex flex-col items-center">
                                <div className="mb-6 md:mb-12 mx-auto animate-float">
                                    <KaiLogo size={80} animated={true} color={kaiState.color} className="md:w-[120px] md:h-[120px]" />
                                </div>

                                <div className="w-full max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-2 gap-3 md:gap-6 px-0 md:px-0">
                                    {prisms.map((prism, i) => (
                                        <PrismCard
                                            key={i}
                                            {...prism}
                                            index={i}
                                            themeMode={theme.mode}
                                            isShattering={isShattering === i}
                                            onClick={() => handleStarterClick(prism.prompt, i)}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {messages.map((msg, i) => {
                    return (
                        <div
                            key={msg.id}
                            className={`flex items-end gap-2 md:gap-4 animate-slide-in-right ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                            style={{ animationDelay: `${i * 50}ms` }}
                        >
                            <div
                                className={`max-w-[90%] md:max-w-[85%] px-5 py-3 md:px-6 md:py-4 text-[15px] leading-relaxed relative ${msg.role === 'user'
                                    ? `rounded-[24px_24px_4px_24px] backdrop-blur-xl border border-white/10 text-white ${theme.mode === 'dark' ? 'bg-white/5' : 'bg-black/80'} shadow-lg`
                                    : `bg-white/80 dark:bg-slate-900/80 backdrop-blur-md rounded-[24px_24px_24px_4px] shadow-sm border border-transparent ${theme.colors.text.main} border-l-[3px] ${chatMode === 'standard' ? prismBorderColor : 'border-l-indigo-400'} shadow-[inset_10px_0_20px_-10px_rgba(0,0,0,0.05)]`
                                    }`}
                            >
                                {msg.isThinking ? (
                                    <ThinkingIndicator mode={chatMode} color={kaiState.color} />
                                ) : msg.role === 'model' ? (
                                    <div className="flex flex-col gap-3">
                                        <SimpleMarkdown content={msg.text} />

                                        {/* Render Consolidated Insights */}
                                        {msg.insights && msg.insights.map((insight, idx) => (
                                            <div key={`insight-${idx}`} className="mt-2">
                                                <InsightCard type={insight.type} items={insight.items} />
                                            </div>
                                        ))}

                                        {/* ACTION BUTTON LOGIC */}
                                        {msg.action && onAction && (
                                            <button
                                                onClick={async () => {
                                                    if (msg.action === 'view_blueprints') {
                                                        onAction('view_blueprints', msg.id);
                                                        return;
                                                    }
                                                    
                                                    setGeneratingId(msg.id);
                                                    try {
                                                        await onAction('generate_blueprints', msg.id);
                                                    } finally {
                                                        setGeneratingId(null);
                                                    }
                                                }}
                                                disabled={generatingId === msg.id}
                                                className={`flex items-center gap-2 px-4 py-2 mt-2 w-fit rounded-xl font-bold text-sm text-white shadow-lg transition-all 
                                                    ${msg.action === 'view_blueprints'
                                                        ? 'bg-green-600 hover:bg-green-500 hover:scale-105 cursor-pointer'
                                                        : `bg-gradient-to-r from-${kaiTheme.name}-500 to-purple-500 ${generatingId === msg.id ? 'opacity-80 cursor-wait' : 'hover:scale-105'}`
                                                    }`}
                                            >
                                                {generatingId === msg.id ? (
                                                    <>
                                                        <Loader2 size={16} className="animate-spin" />
                                                        Generating Prototypes...
                                                    </>
                                                ) : msg.action === 'view_blueprints' ? (
                                                    <>
                                                        <ArrowRight size={16} />
                                                        View Future Prototypes
                                                    </>
                                                ) : (
                                                    <>
                                                        <Sparkles size={16} />
                                                        Generate Future Prototypes
                                                    </>
                                                )}
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    msg.text
                                )}
                            </div>
                        </div>
                    );
                })}

                {isLoading && !messages.some(m => m.isThinking) && (
                    <div className="flex items-end gap-2 md:gap-4 animate-fade-in-up px-2">
                        <div className={`px-6 py-4 rounded-[24px_24px_24px_4px] bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm`}>
                            <ThinkingIndicator mode={chatMode} color={kaiState.color} />
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* 3. Input Capsule - Flex Shrink (Not Absolute) */}
            <div className="shrink-0 p-4 md:p-6 w-full flex justify-center z-30">
                <form
                    onSubmit={handleSubmit}
                    id="chat-input-area"
                    className={`flex items-center gap-2 p-2 pl-4 rounded-full transition-all duration-300 w-full max-w-3xl backdrop-blur-2xl border shadow-2xl ${theme.mode === 'dark'
                        ? 'bg-slate-900/60 border-white/10 shadow-[0_0_40px_rgba(0,0,0,0.5)]'
                        : 'bg-white/60 border-white/40 shadow-[0_8px_32px_rgba(99,102,241,0.15)]'
                        }`}
                >
                    {chatMode === 'standard' && (
                        <button
                            type="button"
                            onClick={onStartVoice}
                            className={`p-2.5 rounded-full transition-colors hover:scale-110 ${theme.mode === 'dark' ? 'text-indigo-400 hover:bg-indigo-500/20' : 'text-indigo-600 hover:bg-indigo-100'}`}
                            title="Voice Mode"
                        >
                            <Mic size={20} className={dynamic.textAccent} />
                        </button>
                    )}

                    <input
                        type="text"
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                        placeholder={
                            chatMode === 'time_capsule' ? "Ask your future self..." :
                                chatMode === 'origin_story' ? "What did you used to do for fun?" :
                                    "Share your thoughts..."
                        }
                        className={`flex-1 bg-transparent border-none focus:ring-0 outline-none px-2 text-base ${theme.colors.text.main} placeholder:opacity-50 min-w-0`}
                        disabled={isLoading}
                    />

                    <button
                        type="submit"
                        disabled={!inputText.trim() || isLoading}
                        className={`p-3 rounded-full transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100 ${chatMode === 'standard' ? dynamic.sendBtn + ' text-white' :
                            chatMode === 'time_capsule' ? 'bg-violet-600 hover:bg-violet-500 text-white shadow-lg shadow-violet-500/30' :
                                'bg-amber-500 hover:bg-amber-400 text-white shadow-lg shadow-amber-500/30'
                            }`}
                    >
                        <ArrowUp size={20} />
                    </button>
                </form>
            </div>

            {/* Disclaimer - Bottom of Input Area */}
            <div className={`text-center text-[10px] font-medium opacity-30 pointer-events-none pb-2 ${theme.colors.text.secondary}`}>
                Kai can make mistakes. Verify important info.
            </div>
        </div>
    );
};